import { useState, useCallback, useEffect } from 'react';
import { getEquipments } from '../../../api/healthAPI';
import { errorToast } from '../../../utils/helper';

// Custom hook for equipment data management with caching
const useEquipments = () => {
  const [equipments, setEquipments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cached, setCached] = useState(false);

  const fetchEquipments = useCallback(
    async (forceRefresh = false) => {
      // Skip if already cached and not forcing refresh
      if (cached && !forceRefresh) return equipments;

      try {
        setLoading(true);
        setError(null);

        const response = await getEquipments({ limit: 100 });

        if (response?.meta?.code === 1 && response?.data) {
          const formattedEquipments = response.data.map((item) => ({
            label: item.name || item.title,
            value: item.id || item._id,
            category: item.category,
            description: item.description
          }));

          setEquipments(formattedEquipments);
          setCached(true);
          return formattedEquipments;
        } else {
          throw new Error(response?.meta?.message || 'Failed to fetch equipments');
        }
      } catch (err) {
        console.error('Error fetching equipments:', err);
        setError(err.message);
        errorToast('Failed to load equipment options');
        return [];
      } finally {
        setLoading(false);
      }
    },
    [cached, equipments]
  );

  // Auto-fetch on first use
  useEffect(() => {
    if (!cached && equipments.length === 0) {
      fetchEquipments();
    }
  }, [fetchEquipments, cached, equipments.length]);

  const refreshEquipments = useCallback(() => {
    return fetchEquipments(true);
  }, [fetchEquipments]);

  return {
    equipments,
    loading,
    error,
    fetchEquipments,
    refreshEquipments,
    cached
  };
};

export default useEquipments;
